import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { useApp } from '../../appContext';
import type { GroundControlLocation, DashboardFilters } from '../../types';
import { AREA_TYPES } from '../../types';
import { formatDate, today, calculateNextInspectionDate } from '../../utils/scheduling';
import FilterSidebar from './FilterSidebar';
import ReportOverlay from './ReportOverlay';
import ConcernOverlay from './ConcernOverlay';
import HistoryOverlay from './HistoryOverlay';

const columnHelper = createColumnHelper<GroundControlLocation>();

const EMPTY_FILTERS: DashboardFilters = {
  locationText: '',
  inspectorText: '',
  lastInspectedFrom: undefined,
  nextDueBefore: undefined,
  areaType: 'All',
  locationQuick: 'All',
  sortField: 'LastInspectedDate',
  sortDir: 'desc',
};

export default function DashboardPage() {
  const {
    locations,
    inspections,
    isLoading,
    lastRefresh,
    refreshData,
    createLocation,
    updateLocation,
    deleteLocation,
    isOnline,
  } = useApp();

  const [editMode, setEditMode] = useState(false);
  const [filters, setFilters] = useState<DashboardFilters>(EMPTY_FILTERS);
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showConcerns, setShowConcerns] = useState(false);
  const [historyLocation, setHistoryLocation] = useState<GroundControlLocation | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  // Auto-refresh (45s, paused in edit mode)
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (editMode) {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
      return;
    }
    autoRefreshRef.current = setInterval(() => { refreshData(); }, 45000);
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    };
  }, [editMode, refreshData]);

  // ── Filtered + sorted data ──────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let data = [...locations];

    if (filters.areaType !== 'All')
      data = data.filter((l) => l.AreaType === filters.areaType);
    if (filters.locationQuick !== 'All')
      data = data.filter((l) => l.Area === filters.locationQuick);
    if (filters.locationText) {
      const q = filters.locationText.toLowerCase();
      data = data.filter((l) => l.Area.toLowerCase().includes(q));
    }
    if (filters.inspectorText) {
      const q = filters.inspectorText.toLowerCase();
      data = data.filter((l) => (l.InspectedBy ?? '').toLowerCase().includes(q));
    }
    if (filters.lastInspectedFrom)
      data = data.filter((l) => l.LastInspectedDate && l.LastInspectedDate >= filters.lastInspectedFrom!);
    if (filters.nextDueBefore)
      data = data.filter((l) => l.NextInspectionDueBy && l.NextInspectionDueBy <= filters.nextDueBefore!);

    data.sort((a, b) => {
      const aVal = a[filters.sortField] ?? '';
      const bVal = b[filters.sortField] ?? '';
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return filters.sortDir === 'asc' ? cmp : -cmp;
    });

    return data;
  }, [locations, filters]);

  const hasActiveFilters =
    !!filters.locationText || !!filters.inspectorText ||
    !!filters.lastInspectedFrom || !!filters.nextDueBefore ||
    filters.areaType !== 'All' || filters.locationQuick !== 'All';

  const concernCount = inspections.filter((i) => i.NumberOfConcerns > 0).length;

  // ── Inline edit handler ──────────────────────────────────────────────────
  const handleFieldChange = async (
    id: string,
    field: keyof GroundControlLocation,
    value: string | number
  ) => {
    setSavingId(id);
    const patch: Partial<GroundControlLocation> = { [field]: value };

    // Auto-calculate next inspection when last inspected date changes
    if (field === 'LastInspectedDate') {
      const loc = locations.find((l) => l.id === id);
      patch.NextInspectionDueBy = calculateNextInspectionDate(value as string, loc?.GroundMatrixScore);
    }

    await updateLocation(id, patch);
    setSavingId(null);
  };

  const handleNewLocation = async () => {
    await createLocation({ Area: 'New Location', AreaType: 'Underground', Enabled: true, NumberOfConcerns: 0 });
  };

  const handleDeleteLocation = async (id: string) => {
    if (!confirm('Delete this location? This cannot be undone.')) return;
    await deleteLocation(id);
  };

  // ── Quick filter options ─────────────────────────────────────────────────
  const uniqueAreaTypes = ['All', ...Array.from(new Set(locations.map((l) => l.AreaType))).sort()];
  const uniqueLocations = ['All', ...Array.from(new Set(locations.map((l) => l.Area))).sort()];

  // ── Column definitions ───────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'rowIndex',
        header: '#',
        cell: ({ row }) => <span className="text-gray-400 text-xs">{row.index + 1}</span>,
        size: 36,
      }),
      columnHelper.accessor('Area', {
        header: () => (
          <span>Location {filters.locationText && <span className="text-[#4C9E00]">🔗</span>}</span>
        ),
        cell: ({ getValue, row }) =>
          editMode ? (
            <input
              type="text"
              defaultValue={getValue()}
              onBlur={(e) => { if (e.target.value !== getValue()) handleFieldChange(row.original.id!, 'Area', e.target.value); }}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm min-w-[120px]"
            />
          ) : (
            <span className="font-medium text-gray-900">{getValue()}</span>
          ),
      }),
      columnHelper.accessor('AreaType', {
        header: 'Area Type',
        cell: ({ getValue, row }) =>
          editMode ? (
            <select
              value={getValue() ?? ''}
              onChange={(e) => handleFieldChange(row.original.id!, 'AreaType', e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            >
              {AREA_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          ) : (
            <span className="bg-[#EBF7E0] text-[#4C9E00] text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
              {getValue()}
            </span>
          ),
        size: 110,
      }),
      columnHelper.accessor('LastInspectedDate', {
        header: () => (
          <span>Last Inspected {filters.lastInspectedFrom && <span className="text-[#4C9E00]">🔗</span>}</span>
        ),
        cell: ({ getValue, row }) =>
          editMode ? (
            <input
              type="date"
              defaultValue={getValue() ?? ''}
              onBlur={(e) => { if (e.target.value) handleFieldChange(row.original.id!, 'LastInspectedDate', e.target.value); }}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            />
          ) : (
            <span className="text-sm text-gray-700 whitespace-nowrap">{formatDate(getValue())}</span>
          ),
        size: 130,
      }),
      columnHelper.accessor('NumberOfConcerns', {
        header: 'Concerns',
        cell: ({ getValue, row }) =>
          editMode ? (
            <input
              type="number" min={0}
              defaultValue={getValue() ?? 0}
              onBlur={(e) => {
                const n = parseInt(e.target.value) || 0;
                if (n !== (getValue() ?? 0)) handleFieldChange(row.original.id!, 'NumberOfConcerns', n);
              }}
              className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center"
            />
          ) : (
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              (getValue() ?? 0) > 0 ? 'bg-red-100 text-red-700' : 'bg-green-50 text-[#4C9E00]'
            }`}>
              {getValue() ?? 0}
            </span>
          ),
        size: 80,
      }),
      columnHelper.accessor('AllRedTaped', {
        header: 'Red Taped',
        cell: ({ getValue, row }) =>
          editMode ? (
            <select
              value={getValue() ?? ''}
              onChange={(e) => handleFieldChange(row.original.id!, 'AllRedTaped', e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            >
              <option value="">—</option>
              {['Yes', 'No', 'N/A'].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
              getValue() === 'Yes' ? 'bg-red-100 text-red-700' :
              getValue() === 'No' ? 'bg-yellow-100 text-yellow-700' :
              'bg-gray-100 text-gray-500'
            }`}>
              {getValue() ?? '—'}
            </span>
          ),
        size: 90,
      }),
      columnHelper.accessor('InspectedBy', {
        header: () => (
          <span>Inspected By {filters.inspectorText && <span className="text-[#4C9E00]">🔗</span>}</span>
        ),
        cell: ({ getValue, row }) =>
          editMode ? (
            <input
              type="text"
              defaultValue={getValue() ?? ''}
              onBlur={(e) => { if (e.target.value !== getValue()) handleFieldChange(row.original.id!, 'InspectedBy', e.target.value); }}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          ) : (
            <span className="text-sm text-gray-600 whitespace-nowrap">{getValue() ?? '—'}</span>
          ),
        size: 130,
      }),
      columnHelper.accessor('GroundMatrixScore', {
        header: 'Matrix Score',
        cell: ({ getValue, row }) =>
          editMode ? (
            <input
              type="number" min={0} max={100}
              defaultValue={getValue() ?? ''}
              onBlur={(e) => {
                const n = parseFloat(e.target.value);
                if (!isNaN(n)) handleFieldChange(row.original.id!, 'GroundMatrixScore', n);
              }}
              className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center"
            />
          ) : (
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              (getValue() ?? 0) >= 60 ? 'bg-green-50 text-[#4C9E00]' : 'bg-orange-100 text-orange-700'
            }`}>
              {getValue() ?? '—'}
            </span>
          ),
        size: 90,
      }),
      columnHelper.accessor('NextInspectionDueBy', {
        header: () => (
          <span>Next Due {filters.nextDueBefore && <span className="text-[#4C9E00]">🔗</span>}</span>
        ),
        cell: ({ getValue, row }) => {
          const val = getValue();
          const isOverdue = val && val < today();
          return editMode ? (
            <input
              type="date"
              defaultValue={val ?? ''}
              onBlur={(e) => { if (e.target.value) handleFieldChange(row.original.id!, 'NextInspectionDueBy', e.target.value); }}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            />
          ) : (
            <span className={`text-sm font-medium whitespace-nowrap ${isOverdue ? 'text-red-600' : 'text-gray-700'}`}>
              {formatDate(val)}
              {isOverdue && ' ⚠️'}
            </span>
          );
        },
        size: 130,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: ({ row }) =>
          savingId === row.original.id ? (
            <svg className="w-4 h-4 animate-spin text-[#4C9E00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          ) : editMode ? (
            <button
              onClick={() => handleDeleteLocation(row.original.id!)}
              className="text-red-400 hover:text-red-600 p-1 rounded transition"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          ) : (
            <button
              onClick={() => setHistoryLocation(row.original)}
              className="text-gray-400 hover:text-[#4C9E00] p-1 rounded transition"
              title="View inspection history"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          ),
        size: 48,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editMode, filters, savingId, locations]
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 56px)' }}>
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
        {/* Top row */}
        <div className="flex items-center gap-2 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-[#4C9E00] leading-tight">
              Ground Control Dashboard
            </h1>
            {lastRefresh && (
              <p className="text-xs text-gray-400">
                Updated {lastRefresh.toLocaleTimeString()}
                {editMode && ' · Auto-refresh paused'}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <button
              onClick={handleNewLocation}
              disabled={!isOnline}
              className="flex items-center gap-1.5 bg-[#4C9E00] text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#3D8000] transition disabled:opacity-50"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New
            </button>

            <button
              onClick={() => setEditMode((e) => !e)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition border ${
                editMode
                  ? 'bg-amber-50 border-amber-400 text-amber-700 hover:bg-amber-100'
                  : 'border-[#4C9E00] text-[#4C9E00] hover:bg-[#EBF7E0]'
              }`}
            >
              {editMode ? (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Grid
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Grid
                </>
              )}
            </button>

            <button
              onClick={() => setShowReport(true)}
              className="flex items-center gap-1 border border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-50 transition"
            >
              🔔 Report
            </button>

            <button
              onClick={() => setShowConcerns(true)}
              className="flex items-center gap-1 border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-50 transition"
            >
              ❗ Concerns
              {concernCount > 0 && (
                <span className="bg-red-100 text-red-700 text-xs font-bold px-1.5 py-0.5 rounded-full ml-1">
                  {concernCount}
                </span>
              )}
            </button>

            <button
              onClick={() => refreshData()}
              disabled={isLoading}
              className="p-1.5 border border-gray-300 rounded-lg text-gray-500 hover:text-[#4C9E00] hover:border-[#4C9E00] transition disabled:opacity-50"
              title="Refresh data"
            >
              <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Second row — quick filters */}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <select
            value={filters.areaType}
            onChange={(e) => setFilters((f) => ({ ...f, areaType: e.target.value }))}
            className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white text-gray-700"
          >
            {uniqueAreaTypes.map((t) => (
              <option key={t} value={t}>{t === 'All' ? 'All Types' : t}</option>
            ))}
          </select>

          <select
            value={filters.locationQuick}
            onChange={(e) => setFilters((f) => ({ ...f, locationQuick: e.target.value }))}
            className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white text-gray-700"
          >
            {uniqueLocations.map((l) => (
              <option key={l} value={l}>{l === 'All' ? 'All Locations' : l}</option>
            ))}
          </select>

          <select
            value={filters.sortField}
            onChange={(e) => setFilters((f) => ({ ...f, sortField: e.target.value as DashboardFilters['sortField'] }))}
            className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white text-gray-700"
          >
            <option value="LastInspectedDate">Sort: Last Inspected</option>
            <option value="NextInspectionDueBy">Sort: Next Due</option>
          </select>

          <button
            onClick={() => setFilters((f) => ({ ...f, sortDir: f.sortDir === 'asc' ? 'desc' : 'asc' }))}
            className="border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-700 hover:border-[#4C9E00] hover:text-[#4C9E00] transition"
          >
            {filters.sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
          </button>

          <button
            onClick={() => setShowFilterSidebar((v) => !v)}
            className={`flex items-center gap-1 border rounded-lg px-2 py-1 text-xs font-medium transition ${
              hasActiveFilters
                ? 'border-[#4C9E00] text-[#4C9E00] bg-[#EBF7E0]'
                : 'border-gray-300 text-gray-600 hover:border-[#4C9E00] hover:text-[#4C9E00]'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters{hasActiveFilters && ' •'}
          </button>

          <span className="text-xs text-gray-400 ml-auto">
            {filteredData.length} / {locations.length} rows
          </span>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto px-2 py-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 gap-2">
            <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Loading data…
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <p>No locations match your filters.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id} className="bg-gray-50 border-b border-gray-200">
                      {hg.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap"
                          style={{ width: header.getSize() }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row, i) => (
                    <tr
                      key={row.id}
                      className={`border-b border-gray-100 transition-colors ${
                        i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                      } ${editMode ? 'hover:bg-amber-50/40' : 'hover:bg-[#EBF7E0]/30'}`}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-3 py-2.5" style={{ width: cell.column.getSize() }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Overlays ─────────────────────────────────────────────────────── */}
      {showFilterSidebar && (
        <FilterSidebar
          filters={filters}
          onFiltersChange={setFilters}
          onClose={() => setShowFilterSidebar(false)}
          emptyFilters={EMPTY_FILTERS}
        />
      )}
      {showReport && <ReportOverlay inspections={inspections} onClose={() => setShowReport(false)} />}
      {showConcerns && <ConcernOverlay inspections={inspections} onClose={() => setShowConcerns(false)} />}
      {historyLocation && (
        <HistoryOverlay
          location={historyLocation}
          inspections={inspections}
          onClose={() => setHistoryLocation(null)}
        />
      )}
    </div>
  );
}
